import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormControlLabel, Grid, IconButton, InputLabel, Link, MenuItem, Select, Snackbar,
    TextField, Tooltip, Typography } from "@mui/material";
import { AddCircle as IconAddCircle, Delete as IconDelete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import diaFeriadoService from "../../services/diaFeriadoService";
import DataGridBase from "../../components/common/DataGridBase";
import DialogYesNo from "../../components/DialogYesNo";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Mantenimiento de dias feriados.
//
// Un feriado se registra de dos formas:
//   Tipo 1 (Dia y Mes): se repite cada anio, por ejemplo el 1 de enero.
//   Tipo 2 (Fecha):     es de una sola vez, con anio.
//
// IdLaborable indica si ese dia se labora o no. Los que no se laboran descuentan
// de los dias laborables del periodo.

// Valores por defecto de la ventana de mantenimiento.
const ENTIDAD_NUEVA = {
    idDiaFeriado: 0,
    descripcion: "",
    tipo: 1,
    dia: 1,
    mes: 1,
    anio: new Date().getFullYear(),
    idLaborable: 0,
};

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Setiembre", "Octubre", "Noviembre", "Diciembre"];

const Index = () => {
    const { isReadOnly } = useMantenimiento("diaFeriado");
    const navigate = useNavigate();

    const [cargando, setCargando] = useState(true);
    const [results, setResults] = useState([]);

    const [openMantenimiento, setOpenMantenimiento] = useState(false);
    const [entity, setEntity] = useState(ENTIDAD_NUEVA);
    const [guardando, setGuardando] = useState(false);

    const [openDialogEliminar, setOpenDialogEliminar] = useState(false);
    const [feriadoSeleccionado, setFeriadoSeleccionado] = useState(null);

    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    const fetchResults = useCallback(async () => {
        setCargando(true);

        const data = await diaFeriadoService.getsDiaFeriado()
            .catch((err) => { console.log("Err: ", err); return []; });

        // El grid pide un id unico por fila.
        setResults((data || []).map((row) => ({ ...row, id: row.idDiaFeriado })));
        setCargando(false);
    }, [])

    useEffect(() => {
        fetchResults();
    }, [])

    // La fecha se muestra como dia/mes cuando el feriado se repite cada anio, y
    // como dia/mes/anio cuando es de una sola vez.
    const fechaFeriado = (row) => {
        if (!row) return "";

        const dia = String(row.dia == null ? "" : row.dia).padStart(2, "0");
        const mes = String(row.mes == null ? "" : row.mes).padStart(2, "0");

        if (row.tipo == 2) return dia + "/" + mes + "/" + row.anio;

        return dia + "/" + mes;
    }

    const columns = [
        { field: 'id', headerName: 'ID', width: 50, minWidth: 45, sortable: true, headerAlign: 'center', align: 'center',
            renderCell: (p) => <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>{p.value != null ? p.value : p.row.id}</span> },
        { field: 'descripcion', headerName: 'Descripcion', width: 260, sortable: false, headerAlign: 'left'
            , renderCell: (cellValues) => (
                <Link component="button" type="button" underline="hover"
                    onClick={() => abrirEdicion(cellValues.row)}
                    sx={{ fontSize: "inherit", verticalAlign: "baseline" }}>
                    {cellValues.row.descripcion}
                </Link>
            )
        },
        { field: 'tipo', headerName: 'Tipo', width: 130, sortable: false, headerAlign: 'center', align: 'center'
            , valueFormatter: (value) => (value == 2 ? "Fecha fija" : "Dia y Mes") },
        { field: 'fechaFeriado', headerName: 'Fecha', width: 130, sortable: false, headerAlign: 'center', align: 'center'
            , valueFormatter: (value, row) => fechaFeriado(row) },
        { field: 'idLaborable', headerName: 'Laborable', width: 110, sortable: false, headerAlign: 'center', align: 'center'
            , valueFormatter: (value) => (value == 1 ? "Si" : "No") },
        // Ultima columna: eliminar el feriado.
        { field: 'eliminar', headerName: '', width: 70, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => (
                <Tooltip title={"Eliminar feriado"}>
                    <span>
                        <IconButton aria-label={"eliminar feriado"} size="small" disabled={isReadOnly}
                            onClick={() => { setFeriadoSeleccionado(cellValues.row); setOpenDialogEliminar(true); }}>
                            <IconDelete fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            )
        },
    ];

    const abrirNuevo = () => {
        setEntity({ ...ENTIDAD_NUEVA, anio: new Date().getFullYear() });
        setOpenMantenimiento(true);
    }

    const abrirEdicion = (feriado) => {
        setEntity({
            idDiaFeriado: feriado.idDiaFeriado,
            descripcion: feriado.descripcion,
            tipo: feriado.tipo,
            dia: feriado.dia == null ? 1 : feriado.dia,
            mes: feriado.mes == null ? 1 : feriado.mes,
            anio: feriado.anio == null ? new Date().getFullYear() : feriado.anio,
            idLaborable: feriado.idLaborable,
        });
        setOpenMantenimiento(true);
    }

    const guardar = async () => {
        if (!entity.descripcion || entity.descripcion.trim() === "") {
            setPropsMessage({ severity: "error", message: "Indique la descripcion del feriado." });
            setOpenMessage(true);
            return;
        }

        if (!entity.dia || !entity.mes) {
            setPropsMessage({ severity: "error", message: "Indique el dia y el mes del feriado." });
            setOpenMessage(true);
            return;
        }

        setGuardando(true);

        const payload = {
            ...entity,
            descripcion: entity.descripcion.trim(),
            // El tipo dia y mes no usa el anio.
            anio: entity.tipo == 2 ? entity.anio : null,
        };

        const id = await diaFeriadoService.addUpdateDiaFeriado(payload)
            .catch((err) => { console.log("Err: ", err); return 0; });

        setGuardando(false);

        if (!id) {
            setPropsMessage({ severity: "error", message: "No se pudo guardar el feriado." });
            setOpenMessage(true);
            return;
        }

        setOpenMantenimiento(false);
        setPropsMessage({ severity: "success", message: "Se guardaron los datos del feriado con exito." });
        setOpenMessage(true);

        fetchResults();
    }

    const eliminar = async () => {
        if (!feriadoSeleccionado) return;

        const eliminado = await diaFeriadoService.deleteDiaFeriado(feriadoSeleccionado.idDiaFeriado)
            .catch((err) => { console.log("Err: ", err); return false; });

        setFeriadoSeleccionado(null);

        if (!eliminado) {
            setOpenDialogEliminar(false);
            return;
        }

        setPropsMessage({ severity: "success", message: "Se elimino el feriado." });
        setOpenMessage(true);

        fetchResults();
    }

    return (
        <div>
            <Grid container>
                <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Dias Feriados
                    </Typography>
                </Grid>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <DataGridBase
                                rows={results}
                                columns={columns}
                                loading={cargando}
                                toolbarButtons={[
                                    {
                                        text: "Nuevo",
                                        icon: <IconAddCircle />,
                                        disabled: isReadOnly,
                                        onClick: abrirNuevo,
                                    }
                                ]}
                                onRowDoubleClick={(event) => {
                                    if (isReadOnly) return;

                                    abrirEdicion(event.row);
                                }}
                                disableRowSelectionOnClick={false}
                                onRowClick={(event) => { setFeriadoSeleccionado(event.row); }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>

            {/* Ventana de mantenimiento del feriado. */}
            <Dialog open={openMantenimiento} fullWidth maxWidth="sm" onClose={() => setOpenMantenimiento(false)}>
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {(entity.idDiaFeriado > 0 ? "Editar" : "Nuevo") + " Dia Feriado"}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={1} alignItems={"center"} sx={{ paddingTop: "12px" }}>
                        <Grid item xs={12} sm={12}>
                            <Box sx={{ fontWeight: 'bold' }} padding={0}>
                                <Typography variant="h7">
                                    Datos Básicos
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <TextField
                                required
                                autoFocus
                                name="descripcion"
                                label="Descripcion"
                                fullWidth
                                value={entity.descripcion}
                                onChange={(event) => setEntity({ ...entity, descripcion: event.target.value })}
                                disabled={isReadOnly || guardando}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl required fullWidth>
                                <InputLabel htmlFor="input-tipo" shrink>Tipo</InputLabel>
                                <Select
                                    native
                                    label="Tipo"
                                    value={entity.tipo}
                                    onChange={(event) => setEntity({ ...entity, tipo: parseInt(event.target.value) })}
                                    inputProps={{ name: 'tipo', id: 'input-tipo' }}
                                    disabled={isReadOnly || guardando}
                                    size="small"
                                >
                                    <option key={1} value={1}>{"Dia y Mes"}</option>
                                    <option key={2} value={2}>{"Fecha fija"}</option>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <FormControl required fullWidth>
                                <InputLabel htmlFor="input-dia" shrink>Dia</InputLabel>
                                <Select
                                    native
                                    label="Dia"
                                    value={entity.dia}
                                    onChange={(event) => setEntity({ ...entity, dia: parseInt(event.target.value) })}
                                    inputProps={{ name: 'dia', id: 'input-dia' }}
                                    disabled={isReadOnly || guardando}
                                    size="small"
                                >
                                    {Array.from({ length: 31 }, (_, i) => i + 1).map((dia) => (
                                        <option key={dia} value={dia}>{dia}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <FormControl required fullWidth>
                                <InputLabel htmlFor="input-mes" shrink>Mes</InputLabel>
                                <Select
                                    native
                                    label="Mes"
                                    value={entity.mes}
                                    onChange={(event) => setEntity({ ...entity, mes: parseInt(event.target.value) })}
                                    inputProps={{ name: 'mes', id: 'input-mes' }}
                                    disabled={isReadOnly || guardando}
                                    size="small"
                                >
                                    {MESES.map((mes, i) => (
                                        <option key={i + 1} value={i + 1}>{mes}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {/* El anio solo se pide cuando el feriado es de una sola fecha. */}
                        {entity.tipo == 2 && (
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    required
                                    name="anio"
                                    label="Anio"
                                    type="number"
                                    fullWidth
                                    value={entity.anio}
                                    onChange={(event) => setEntity({ ...entity, anio: parseInt(event.target.value) })}
                                    disabled={isReadOnly || guardando}
                                    size="small"
                                    inputProps={{ min: 2000, max: 2100 }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={8}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="idLaborable"
                                        checked={entity.idLaborable == 1}
                                        onChange={(event) => setEntity({ ...entity, idLaborable: event.target.checked ? 1 : 0 })}
                                        disabled={isReadOnly || guardando}
                                        size="small"
                                    />
                                }
                                label={"Es laborable (no descuenta del conteo)"}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setOpenMantenimiento(false)}>{"Cancelar"}</Button>
                    <Button variant="contained" disabled={guardando} onClick={guardar}>{"Guardar"}</Button>
                </DialogActions>
            </Dialog>

            <DialogYesNo
                open={openDialogEliminar}
                titulo={"Eliminar Dia Feriado"}
                texto={"Se eliminara el feriado y dejara de descontarse en el conteo de dias "
                    + "laborables de los periodos. Desea continuar?"}
                setYes={eliminar}
                setNo={() => { setFeriadoSeleccionado(null); }}
                openClose={() => {
                    setOpenDialogEliminar(!openDialogEliminar);

                    if (openDialogEliminar) setFeriadoSeleccionado(null);
                }}
            />

            <Snackbar open={openMessage} autoHideDuration={5000} onClose={() => setOpenMessage(!openMessage)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setOpenMessage(!openMessage)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default Index;
